import { api } from '@/lib/axios';
import { type PdfMode, renderPdf } from '@/lib/pdf';
import type { Page } from '@/services/chantiers';

export type DepenseCategorie =
  | 'MATERIAUX' | 'CARBURANT' | 'LOCATION_MATERIEL' | 'SOUS_TRAITANCE'
  | 'FOURNITURES_BUREAU' | 'FRAIS_ADMINISTRATIFS' | 'TRANSPORT' | 'ASSURANCE'
  | 'IMPOTS_TAXES' | 'HONORAIRES' | 'TELECOMMUNICATIONS' | 'ENERGIE'
  | 'ENTRETIEN_REPARATION' | 'AUTRE';

export type AxeAnalytique = 'CHANTIER' | 'CENTRE_COUT';
export type ModePaiement = 'ESPECES' | 'CHEQUE' | 'VIREMENT' | 'CARTE' | 'PRELEVEMENT';
export type DepenseStatut = 'BROUILLON' | 'VALIDEE' | 'PAYEE';

export const DEPENSE_CATEGORIES: DepenseCategorie[] = [
  'MATERIAUX', 'CARBURANT', 'LOCATION_MATERIEL', 'SOUS_TRAITANCE', 'FOURNITURES_BUREAU',
  'FRAIS_ADMINISTRATIFS', 'TRANSPORT', 'ASSURANCE', 'IMPOTS_TAXES', 'HONORAIRES',
  'TELECOMMUNICATIONS', 'ENERGIE', 'ENTRETIEN_REPARATION', 'AUTRE',
];

export const MODES_PAIEMENT: ModePaiement[] = ['ESPECES', 'CHEQUE', 'VIREMENT', 'CARTE', 'PRELEVEMENT'];

export const DEPENSE_STATUTS: DepenseStatut[] = ['BROUILLON', 'VALIDEE', 'PAYEE'];

/** Taux de TVA autorisés (réduit / normal algérien) — fraction décimale. */
export const TVA_TAUX: { value: number; label: string }[] = [
  { value: 0, label: '0 %' },
  { value: 0.09, label: '9 %' },
  { value: 0.19, label: '19 %' },
];

export interface DepenseDTO {
  id: string;
  reference: string;
  date_depense: string;
  libelle: string;
  categorie: DepenseCategorie;
  numero_facture: string | null;
  montant_ht: number;
  tva_pct: number;
  tva_montant: number;
  montant_ttc: number;
  tva_deductible: boolean;
  axe_analytique: AxeAnalytique;
  chantier_id: string | null;
  centre_cout_id: string | null;
  tiers_id: string | null;
  tiers_nom_libre: string | null;
  mode_paiement: ModePaiement;
  reference_paiement: string | null;
  justificatif_url: string | null;
  comptabilisee: boolean;
  compte_charge: string | null;
  statut: DepenseStatut;
  notes: string | null;
}

export interface DepenseInput {
  date_depense: string;
  libelle: string;
  categorie: DepenseCategorie;
  numero_facture?: string | null;
  montant_ht: number;
  tva_pct: number;
  tva_deductible?: boolean;
  axe_analytique: AxeAnalytique;
  chantier_id?: string | null;
  centre_cout_id?: string | null;
  tiers_id?: string | null;
  tiers_nom_libre?: string | null;
  mode_paiement: ModePaiement;
  reference_paiement?: string | null;
  justificatif_url?: string | null;
  comptabilisee: boolean;
  notes?: string | null;
}

export interface DepenseFilters {
  q?: string;
  categorie?: DepenseCategorie;
  axe?: AxeAnalytique;
  chantier_id?: string;
  centre_cout_id?: string;
  tiers_id?: string;
  mode_paiement?: ModePaiement;
  statut?: DepenseStatut;
  comptabilisation?: 'toutes' | 'comptabilisees' | 'non_comptabilisees';
  mois?: number;
  annee?: number;
  archives?: boolean;
  page?: number;
  size?: number;
}

export interface DepenseStats {
  total_mois: number;
  total_mois_precedent: number;
  total_chantiers: number;
  total_centres_cout: number;
  total_non_comptabilise: number;
  pct_non_comptabilise: number;
  par_categorie: Array<{ categorie: string; total: number }>;
}

export async function listDepenses(filters: DepenseFilters = {}): Promise<Page<DepenseDTO>> {
  const params = { ...filters };
  if (params.comptabilisation === 'toutes') delete params.comptabilisation;
  const { data } = await api.get<Page<DepenseDTO>>('/depenses', { params });
  return data;
}

export async function createDepense(payload: DepenseInput): Promise<DepenseDTO> {
  const { data } = await api.post<DepenseDTO>('/depenses', payload);
  return data;
}

export async function updateDepense(id: string, payload: Partial<DepenseInput> & { statut?: DepenseStatut; is_archived?: boolean }): Promise<DepenseDTO> {
  const { data } = await api.put<DepenseDTO>(`/depenses/${id}`, payload);
  return data;
}

export async function deleteDepense(id: string): Promise<void> {
  await api.delete(`/depenses/${id}`);
}

/** Ouvre/télécharge la fiche PDF d'une seule dépense (toutes ses informations). */
export async function openDepensePdf(id: string, mode: PdfMode = 'open'): Promise<void> {
  await renderPdf(`/depenses/${id}/pdf`, undefined, mode);
}

/** Ouvre/télécharge l'état imprimable des dépenses filtrées (PDF, en-tête + logo). */
export async function openEtatDepensesPdf(filters: DepenseFilters = {}, mode: PdfMode = 'open'): Promise<void> {
  const params: Record<string, unknown> = { ...filters };
  if (params.comptabilisation === 'toutes') delete params.comptabilisation;
  delete params.page;
  delete params.size;
  await renderPdf('/depenses/etat/pdf', params, mode);
}

export async function getDepenseStats(annee?: number, mois?: number): Promise<DepenseStats> {
  const { data } = await api.get<DepenseStats>('/depenses/stats', { params: { annee, mois } });
  return data;
}
